import React from 'react'
import {
  Dropdown,
  IDropdownOption,
  Label,
  Separator,
  TextField,
} from 'office-ui-fabric-react'

export function MultiPositionWithFloatingForm({
  integrationTypeConfig,
  onIntegrationTypeConfigChange,
  disabled,
  errorCode,
}: {
  integrationTypeConfig: any
  onIntegrationTypeConfigChange: (config: any) => void
  disabled: boolean
  errorCode: string
}) {
  const isAfterElement = integrationTypeConfig.position === 'afterElement'
  return (
    <>
      <Dropdown
        label="Position"
        disabled={disabled}
        selectedKey={isAfterElement ? 'afterElement' : 'inContainer'}
        options={[
          { key: 'inContainer', text: 'Dynamic in container' },
          { key: 'afterElement', text: 'After element' },
        ]}
        onChange={(
          _event: React.FormEvent<HTMLDivElement>,
          item: IDropdownOption | undefined
        ): void => {
          if (!item) return
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            position: item.key as string,
          })
        }}
      />
      <TextField
        label={isAfterElement ? 'Insert after selector' : 'Selector'}
        placeholder={isAfterElement ? 'article p:last-of-type' : 'article p'}
        errorMessage={errorCode === 'invalidSelector' ? 'Invalid selector' : ''}
        value={integrationTypeConfig.selector || ''}
        autoComplete="off"
        readOnly={false}
        disabled={disabled}
        onChange={(
          _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
          newValue?: string
        ): void => {
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            selector: newValue || '',
          })
        }}
      />
      <Separator />
      <Label>Floating</Label>
      <div style={{ display: 'grid', gap: '16px' }}>
        <div
          style={{
            display: 'grid',
            gap: '16px 28px',
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          <Dropdown
            label="Horizontal Container Position"
            disabled={disabled}
            selectedKey={
              integrationTypeConfig.horizontalContainerPosition || 'right'
            }
            options={[
              { key: 'left', text: 'Left' },
              { key: 'right', text: 'Right' },
            ]}
            onChange={(
              _event: React.FormEvent<HTMLDivElement>,
              item: IDropdownOption | undefined
            ): void => {
              if (!item) return
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                horizontalContainerPosition: item.key as string,
              })
            }}
          />
          <Dropdown
            label="Vertical Container Position"
            disabled={disabled}
            selectedKey={
              integrationTypeConfig.verticalContainerPosition || 'bottom'
            }
            options={[
              { key: 'top', text: 'Top' },
              { key: 'bottom', text: 'Bottom' },
            ]}
            onChange={(
              _event: React.FormEvent<HTMLDivElement>,
              item: IDropdownOption | undefined
            ): void => {
              if (!item) return
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                verticalContainerPosition: item.key as string,
              })
            }}
          />
          <Dropdown
            label="Horizontal Close Button Position"
            disabled={disabled}
            selectedKey={
              integrationTypeConfig.horizontalCloseButtonPosition || 'right'
            }
            options={[
              { key: 'left', text: 'Left' },
              { key: 'right', text: 'Right' },
            ]}
            onChange={(
              _event: React.FormEvent<HTMLDivElement>,
              item: IDropdownOption | undefined
            ): void => {
              if (!item) return
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                horizontalCloseButtonPosition: item.key as string,
              })
            }}
          />
          <Dropdown
            label="Vertical Close Button Position"
            disabled={disabled}
            selectedKey={
              integrationTypeConfig.verticalCloseButtonPosition || 'top'
            }
            options={[
              { key: 'top', text: 'Top' },
              { key: 'bottom', text: 'Bottom' },
            ]}
            onChange={(
              _event: React.FormEvent<HTMLDivElement>,
              item: IDropdownOption | undefined
            ): void => {
              if (!item) return
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                verticalCloseButtonPosition: item.key as string,
              })
            }}
          />
        </div>
        <div
          style={{
            display: 'grid',
            gap: '28px',
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          <TextField
            label="Maximum width"
            placeholder="640"
            type="number"
            min={1}
            errorMessage={
              errorCode === 'invalidWidthHeight'
                ? 'Must be a positive number'
                : ''
            }
            value={
              integrationTypeConfig.maxWidth != null
                ? String(integrationTypeConfig.maxWidth)
                : ''
            }
            autoComplete="off"
            disabled={disabled}
            onChange={(
              _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
              newValue?: string
            ): void => {
              const parsed =
                newValue != null && newValue !== ''
                  ? parseInt(newValue, 10)
                  : undefined
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                maxWidth: parsed != null && !isNaN(parsed) ? parsed : undefined,
              })
            }}
          />
          <TextField
            label="Maximum height"
            placeholder="480"
            type="number"
            min={1}
            errorMessage={
              errorCode === 'invalidWidthHeight'
                ? 'Must be a positive number'
                : ''
            }
            value={
              integrationTypeConfig.maxHeight != null
                ? String(integrationTypeConfig.maxHeight)
                : ''
            }
            autoComplete="off"
            disabled={disabled}
            onChange={(
              _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
              newValue?: string
            ): void => {
              const parsed =
                newValue != null && newValue !== ''
                  ? parseInt(newValue, 10)
                  : undefined
              onIntegrationTypeConfigChange({
                ...integrationTypeConfig,
                maxHeight:
                  parsed != null && !isNaN(parsed) ? parsed : undefined,
              })
            }}
          />
        </div>
        <TextField
          label="Close button text"
          placeholder="Close ad"
          value={integrationTypeConfig.closeButtonText || ''}
          autoComplete="off"
          readOnly={false}
          disabled={disabled}
          onChange={(
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
          ): void => {
            onIntegrationTypeConfigChange({
              ...integrationTypeConfig,
              closeButtonText: newValue || '',
            })
          }}
        />
      </div>
      <Separator />
      <TextField
        label="Custom CSS"
        placeholder=".carodaContainer { float: none; }"
        value={
          integrationTypeConfig.playerOptions
            ? integrationTypeConfig.playerOptions.css || ''
            : ''
        }
        autoComplete="off"
        readOnly={false}
        disabled={disabled}
        multiline={true}
        rows={5}
        onChange={(
          _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
          newValue?: string
        ): void => {
          onIntegrationTypeConfigChange({
            ...integrationTypeConfig,
            playerOptions: {
              ...(integrationTypeConfig.playerOptions || {}),
              css: newValue || '',
            },
          })
        }}
      />
    </>
  )
}
